import { Alignment, CurrencySettings, ModelRow, UrlUtil } from "@mcleod/core";
import { AutogenLayoutOpportunity } from "./autogen/AutogenLayoutOpportunity";
import { BlurEvent, ClickEvent, DataDisplayEvent, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DecoratorFactory, DropdownSelectionEvent, Label, Layout, LookupModelSelectionEvent, SlideoutDecorator, Tab, Table, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { LinkedModel } from "@mcleod/core/src/ModelRow";
import { ModelProspect } from "./models/ModelProspect";
import { ModelUsers } from "@mcleod/common";
import { AssignMarketPlan } from "./AssignMarketPlan";
import { OpportunityFindNear } from "./OpportunityFindNear";


export class Opportunity extends AutogenLayoutOpportunity {
    mcEnabled: boolean = false;

    onLoad(): void | Promise<void> {
        this.mcEnabled = CurrencySettings.getSingleton().multiCurrencyEnabled();
        this.textboxCurrency.visible = this.mcEnabled;
        this.styleTab(this.tabActivities);
        this.styleTab(this.tabComments);
        this.styleTab(this.tabCharges);
        this.configureUpdateTabCount(this.tabActivities, this.layoutTableActivities.tableActivities);
        this.configureUpdateTabCount(this.tabComments, this.layoutTableComment.tableComments);
        this.configureUpdateTabCount(this.tabCharges, this.tableOtherChargeRate);
        this.layoutTableComment.displayCopyOrderFields = false;
        this.layoutOrigDestRates.buttonAdjustRate.visible = false;
    }

    textboxOpportunityStatusOnDataDisplay(event: DataDisplayEvent): void {
            const status = event.rowData?.get("opportunity_status", null);
            const awarded = status === 'A';
            if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                this.panelGeneral.forEveryChildComponent((component) => {
                    component.enabled = !awarded;
                });
            }
        }

    textboxOpportunityStatusAfterDropdownSelection(event: DropdownSelectionEvent): void {
        const status = event.selection?.value;
        this.syncOpportunityStatus(status);
    }

    syncOpportunityStatus(status) {
        const awarded = status === 'A';
        if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            this.panelGeneral.forEveryChildComponent((component) => {
                component.enabled = !awarded;
            });
        }
    }

    textboxPrimaryContactBeforeLookupModelSearch(event): void {
        const parentRowId = this.activeRow?.get("customer_id", null);
        if (!parentRowId) {
            event.stop();
            return;
        }
        event.filter.parent_row_id = parentRowId;
    }

    textboxCustomerIdOnDataDisplay(event: DataDisplayEvent): void {
        const customerId = event.rowData?.get("customer_id", null);
        if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            this.syncCustomerId(customerId);
        }
    }

    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent): void {
        const customerId = event.selection?.get("id", null);
        if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            this.syncCustomerId(customerId);
        }
    }

    syncCustomerId(customerId) {
        this.textboxPrimaryContact.enabled = !!customerId && this.activeRow?.get("opportunity_status", null) !== 'A';
        if (!this.textboxPrimaryContact.enabled && this.activeRow?.get("opportunity_status", null) !== 'A') {
            this.textboxPrimaryContact.clear();
            this.activeRow?.set("contact_id", null);
        }
        this.layoutTableActivities.prospectId = customerId;
        this.layoutTableActivities.buttonAddAction.enabled = !!customerId;
    }

    textboxSalespersonIdOnDataDisplay(event: DataDisplayEvent): void {
        const salesManagerId = event.rowData?.get("salesperson.sales_manager_id", null);
        this.syncSalesManager(salesManagerId);
    }

    textboxSalespersonIdOnLookupModelSelection(event: LookupModelSelectionEvent): void {
        const salesManagerId = event.selection?.get("sales_manager_id", null);
        this.syncSalesManager(salesManagerId);
    }

    syncSalesManager(salesManagerId) {
        if (salesManagerId) {
            new ModelUsers().searchSingle({ id: salesManagerId }, "common/UserLookup").then(row => {
                this.textboxSalesManager.visible = !!row;
                this.textboxSalesManager.text = row?.get("name", "") || "";
            });
        } else {
            this.textboxSalesManager.visible = false;
        }
    }

    textboxOrderTypeBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxTrailerType. */
    textboxTrailerTypeBeforeLookupModelSearch(event) {
        event.filter.applies_to = 'L';
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxTractorType. */
    textboxTractorTypeBeforeLookupModelSearch(event) {
        event.filter.applies_to = 'T';
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxTieredTable. */
    textboxTieredTableBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
        if (this.mcEnabled) {
            event.filter.currency_type = this.activeRow?.get('currency');
        }
    }

    tableOtherChargeRateOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.setEnabledDisabled(tableRow);
    }

    setEnabledDisabled(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        const textBoxUnits = tableRow.findComponentById("textboxUnits") as Textbox;
        switch (modelRow.get("calc_method")) {
            case "D":
                textBoxUnits.enabled = false;
                break;
            case "P":
                textBoxUnits.enabled = false;
                break;
            case "F":
                textBoxUnits.enabled = true;
                break;
            case "C":
            case "T":
                textBoxUnits.enabled = false;
                break;
            default:
                break;
        }
    }

    textboxRateFieldOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        this.rateFieldChanged(tableRow);
    }

    textboxCalculationMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            const tableRow = TableRow.getContainingTableRow(event.target)
            this.rateFieldChanged(tableRow);
        }
    }

    textboxChargeCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const modelRow = tableRow.data as ModelRow;

        if (event.selection) {
            modelRow.set("descr", event.selection.get("descr"));
            modelRow.set("calc_method", event.selection.get("charge_rate_method"));
            switch (event.selection.get("charge_rate_method")) {
                case "D":
                    modelRow.set("units", 0);
                    modelRow.set("rate", event.selection.get("amount"))
                    break;
                case "P":
                    modelRow.set("units", 0)
                    modelRow.set("rate", event.selection.get("rate"))
                    break;
                case "F":
                    modelRow.set("units", 1)
                    modelRow.set("rate", event.selection.get("amount"))
                    break;
                case "C":
                case "T":
                    modelRow.set("rate", event.selection.get("amount"))
                    break;
                default:
                    break;
            }
            this.rateFieldChanged(tableRow);
        }
    }

    rateFieldChanged(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        switch (modelRow.get("calc_method")) {
            case "D":
                modelRow.set("units", 0)
                this.calculateNonPercentRate(modelRow)
                break;
            case "P":
                if (modelRow.get("units") != null && modelRow.get("rate") != null) {
                    modelRow.set("amount", modelRow.get("units") * (1 / modelRow.get("rate")))
                }
                else {
                    modelRow.set("amount", 0);
                }
                break;
            case "F":
                if (modelRow.get("units") == null) {
                    modelRow.set("units", 1);
                }
                this.calculateNonPercentRate(modelRow)
                break;
            case "C":
            case "T":
                modelRow.set("units", 0);
                this.calculateNonPercentRate(modelRow)
                break;
            default:
                break;
        }
        this.setEnabledDisabled(tableRow)
        tableRow.displayComponentValues()
    }

    calculateNonPercentRate(modelRow: ModelRow) {
        const amount = modelRow.get("units") * modelRow.get("rate");
        if (!isNaN(amount)) {
            modelRow.set("amount", amount);
        } else {
            modelRow.set("amount", null);
        }
    }

    /** This is an event handler for the afterModeChange event of sourceRate. */
    sourceOpportunityAfterModeChange(event: DataSourceModeChangeEvent) {
        const isSearch = event.dataSource.mode === DataSourceMode.SEARCH;
        this.layoutOrigDestRates.buttonAdd.visible = !isSearch;
        this.layoutOrigDestRates.buttonSearch.visible = isSearch;
        this.buttonMarketPlan.visible = !isSearch;

        if (event.oldMode === DataSourceMode.ADD && event.newMode === DataSourceMode.UPDATE) {
            const childLinks: LinkedModel[] = this.sourceOpportunity.activeRow?.data["_child_links"];
            childLinks?.forEach(linkedModel => linkedModel.rows?.forEach(row => row._appending = false));
        }

        if (event.oldMode === DataSourceMode.NONE && event.newMode === DataSourceMode.ADD) {
            this.dataHeader.showSave = false;
            const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
            if (urlParams?.prospect) {
                new ModelProspect().searchSingle({ id: urlParams.prospect }).then(prospect => {
                    if (prospect) {
                        this.activeRow.set("effective_date", new Date());
                        this.textboxEffectiveDate.displayComponentData(this.activeRow, null, 0);

                        if (!prospect.isNull("primary_contact")) {
                            const primaryContact = prospect.get("primary_contact");
                            this.activeRow.setValueAndLookupModelData(
                                "primary_contact",
                                primaryContact,
                                { name: primaryContact }
                            );
                            this.textboxPrimaryContact.displayComponentData(this.activeRow, null, 0);
                        }

                        this.activeRow.set("customer_id", prospect.get("id"));
                        this.applyLookupModelData(this.textboxCustomerId, this.activeRow);

                        this.activeRow.set("salesperson_id", prospect.get("salesperson_id"));
                        if (!prospect.isNull("sales_manager_id")) {
                            this.activeRow.set("salesperson.sales_manager_id", prospect.get("sales_manager_id"));
                        }
                        this.applyLookupModelData(this.textboxSalespersonId, this.activeRow);

                        if (!prospect.isNull("mark_plan_id")) {
                            this.activeRow.set("mark_plan_id", prospect.get("mark_plan_id"));
                            this.applyLookupModelData(this.textboxMarkPlanId, this.activeRow);
                        }
                    }
                });
            }
        }
    }

    textboxPrimaryContactOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.selection) {
            this.activeRow.set("contact_id", event.selection.get("id"));
        } else {
            this.activeRow.set("contact_id", null);
        }
    }

    /** This is an event handler for the afterExecution event of sourceRate. */
    sourceOpportunityAfterExecution(event: DataSourceExecutionEvent) {
        const rate = event.data;
        this.layoutOrigDestRates.rateId = rate?.getString('id', null);
        this.layoutOrigDestRates.customerId = rate?.getString('customer_id', null);
        this.layoutTableActivities.opportunityId = rate?.getNumber('id', null);
    }

    buttonMarketPlanOnClick(event: ClickEvent) {
        const payeeId = this.activeRow.get("customer_id");
        AssignMarketPlan.showModal(payeeId, this.activeRow.getNumber("id").toString(), () => {
            this.mainDataSource.search(this.mainDataSource.lastSearch);
        });
    }

    buttonFindNearOnClick(event: ClickEvent) {
        const customerId = this.activeRow?.get("customer_id", null);
        const layout = Layout.getLayout("lme/crm/OpportunityFindNear", { displayingFromToolbox: true }) as OpportunityFindNear;
        layout.addLayoutLoadListener(event => {
            layout.customerId = customerId;
            const decorated = DecoratorFactory.decoratePage(layout, null) as Layout;
            new SlideoutDecorator({
                layout: decorated,
                title: "Find Near Opportunities",
                transitionOptions: { direction: Alignment.BOTTOM },
                displayOverlay: true,
                overlayProps: { closeOnClickOff: false, coverPageHeader: false },
                fillHorizontalSpace: true,
                fillVerticalSpace: true
            });
        });
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    configureUpdateTabCount(tab: Tab, table: Table) {
        const updateCounts = (event) => this.updateTabCountLabel(tab, table);
        table.addContentsChangedListener(updateCounts);
        table.addRowCreateListener(updateCounts);
        table.dataSource.addAfterExecutionListener(updateCounts);
    }

    updateTabCountLabel(tab: Tab, table: Table) {
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.dataSource.rowCount + "";
        }
    }

    private applyLookupModelData(textbox: Textbox, modelRow: ModelRow): Promise<void> {
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        return lookupLayout.ensureLoaded().then(() => {
            const fieldValue = modelRow.get(textbox.field);
            const filter = {
                [textbox.lookupModelResultField]: (fieldValue.indexOf(",") >= 0) ? `in ${fieldValue}` : fieldValue
            };
            return lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                if (response?.modelRows?.length === 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows[0]);
                } else if (response?.modelRows?.length > 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows.map(row => ({
                        [textbox.lookupModelDisplayField]: row.get(textbox.lookupModelDisplayField),
                        [textbox.lookupModelResultField]: row.get(textbox.lookupModelResultField)
                    })));
                } else {
                    const lookupModelData = new ModelRow(modelRow._modelPath, false);
                    lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                    lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), lookupModelData);
                }
            });
        })
    }
}
